import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import {
  NavIcon,
  PageHeader,
  orgLogoUrl,
  orgLogoHorizontalUrl,
  useBrandTokens,
  type ShellOutletContext,
} from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qInvoicePrintSettings,
  mapUIToInvoicePrintSettingsColumns,
  INVOICE_PRINT_DEFAULTS,
  qAppConfig,
  getPrintBranding,
  writePrintBranding,
  letterheadFromBranding,
  normalizePrintFontColor,
  type PrintBrandingConfig,
  type PrintLetterheadInfo,
  type InvoicePrintSettingsRow,
  type InvoicePrintSettingsUI,
  type InvoicePrintTemplateId,
  type InvoicePrintLogoSource,
  type AppConfigRow,
} from '@helios/data-access';
import { INVOICE_SYSTEM_TEMPLATES, type InvoiceSystemTemplate } from './system-templates';
import {
  InvoicePrintLayout,
  type InvoicePrintLineItem,
  type InvoicePrintPayment,
} from './InvoicePrintLayout';
import { resolveInvoicePrintSettings } from './print-utils';
import { printHtmlViaIframe } from '../prescription-print/print-utils';

// ─── Steps ───

const STEPS = [
  { num: 1, label: 'Template' },
  { num: 2, label: 'Branding' },
  { num: 3, label: 'Sections' },
  { num: 4, label: 'Preview & Save' },
] as const;

type StepNum = (typeof STEPS)[number]['num'];

// ─── Sample data for preview ───

const SAMPLE_ITEMS: InvoicePrintLineItem[] = [
  {
    name: 'Dermatology Consultation',
    description: 'Initial 30-minute consult',
    qty: 1,
    unitPrice: 1500,
    discount: 0,
    taxRate: 0,
    amount: 1500,
  },
  {
    name: 'Chemical Peel — Glycolic',
    description: 'Single session',
    qty: 1,
    unitPrice: 3500,
    discount: 350,
    taxRate: 0,
    amount: 3150,
  },
  {
    name: 'Vitamin C Serum 30ml',
    description: 'Take-home',
    qty: 2,
    unitPrice: 1200,
    discount: 0,
    taxRate: 0,
    amount: 2400,
  },
];

const SAMPLE_PAYMENTS: InvoicePrintPayment[] = [
  { amount: 5000, method: 'Cash', paidAtIso: new Date().toISOString() },
];

function sampleTotals() {
  const subtotal = SAMPLE_ITEMS.reduce((s, i) => s + i.qty * i.unitPrice, 0);
  const discountTotal = SAMPLE_ITEMS.reduce((s, i) => s + i.discount, 0);
  const grandTotal = SAMPLE_ITEMS.reduce((s, i) => s + i.amount, 0);
  const paymentsTotal = SAMPLE_PAYMENTS.reduce((s, p) => s + p.amount, 0);
  return {
    subtotal,
    discountTotal,
    taxTotal: 0,
    grandTotal,
    paymentsTotal,
    balanceDue: Math.max(0, grandTotal - paymentsTotal),
  };
}

const SAMPLE_TOTALS = sampleTotals();

function sampleMoney(amount: number) {
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ─── A4 dimensions ───

const A4_WIDTH_PX = (210 * 96) / 25.4;
const A4_HEIGHT_PX = (297 * 96) / 25.4;

// ─── Page ───

export function InvoicePrintWizardPage() {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {};

  // Load saved org-default settings.
  const { data: settingsRows = [] } =
    usePowerSyncQuery<InvoicePrintSettingsRow>(qInvoicePrintSettings());
  const savedRow = settingsRows[0] ?? null;

  // Load app_config so we can pre-fill branding for first-time setup.
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const appConfig = appConfigRows[0] ?? null;
  // PRD.284 — print font color + letterhead branding, edited here as a live
  // draft (persisted to the shared app_config.extra_json.print_branding blob on
  // Save). `brandingDraft` is null until the user edits a branding field.
  const savedBranding = useMemo(() => getPrintBranding(appConfig), [appConfig]);
  const [brandingDraft, setBrandingDraft] = useState<PrintBrandingConfig | null>(null);
  const effectiveBranding = brandingDraft ?? savedBranding;
  const effectiveFontColor = effectiveBranding.printFontColor;
  const effectiveLetterhead = useMemo(
    () => letterheadFromBranding(effectiveBranding, appConfig?.org_localname),
    [effectiveBranding, appConfig],
  );
  const patchBranding = useCallback(
    (patch: Partial<PrintBrandingConfig>) => {
      setBrandingDraft((prev) => ({ ...(prev ?? savedBranding), ...patch }));
      setSaveStatus('idle');
    },
    [savedBranding],
  );

  const [step, setStep] = useState<StepNum>(1);
  const [draft, setDraft] = useState<InvoicePrintSettingsUI | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    if (draft) return;
    if (savedRow || appConfig) {
      setDraft(resolveInvoicePrintSettings(savedRow, appConfig));
    }
  }, [savedRow, appConfig, draft]);

  const effective = useMemo<InvoicePrintSettingsUI>(() => {
    if (draft) return draft;
    return { id: '', ...INVOICE_PRINT_DEFAULTS };
  }, [draft]);

  const setDraftPatch = useCallback((patch: Partial<InvoicePrintSettingsUI>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    setSaveStatus('idle');
  }, []);

  const handleSave = useCallback(async () => {
    if (!db || !orgId || !draft) return;
    setSaving(true);
    setSaveStatus('idle');
    try {
      const cols = mapUIToInvoicePrintSettingsColumns(draft);
      const now = new Date().toISOString();
      const SQL_COLS = [
        'template_id',
        'logo_source',
        'clinic_name',
        'clinic_address',
        'clinic_phone',
        'clinic_email',
        'clinic_website',
        'tax_id',
        'footer_text',
        'show_patient_demographics',
        'show_patient_number',
        'show_invoice_number',
        'show_invoice_date',
        'show_visit_date',
        'show_provider',
        'show_item_quantity',
        'show_item_unit_price',
        'show_item_discount',
        'show_item_tax',
        'show_item_amount',
        'show_subtotal',
        'show_discount_total',
        'show_tax_total',
        'show_payments',
        'show_balance_due',
        'show_footer_notes',
        'show_signature_block',
      ] as const;
      const values = SQL_COLS.map((c) => (cols as Record<string, unknown>)[c]);

      if (savedRow) {
        const setClause = SQL_COLS.map((c) => `${c} = ?`).join(', ');
        await db.execute(
          `UPDATE invoice_print_settings SET ${setClause}, updated_at = ?, updated_by = ? WHERE id = ?`,
          [...values, now, user?.uid ?? null, savedRow.id],
        );
      } else {
        const id = crypto.randomUUID();
        const insertCols = [
          'id',
          'org_id',
          'clinic_id',
          ...SQL_COLS,
          'created_at',
          'updated_at',
          'updated_by',
        ];
        const placeholders = insertCols.map(() => '?').join(', ');
        await db.execute(
          `INSERT INTO invoice_print_settings (${insertCols.join(', ')}) VALUES (${placeholders})`,
          [id, orgId, null, ...values, now, now, user?.uid ?? null],
        );
      }

      // PRD.284 — persist the print branding (font color + letterhead fields)
      // into the shared app_config.extra_json.print_branding blob.
      if (brandingDraft != null) {
        const existing = await db.getAll<{ extra_json: string | null }>(
          `SELECT extra_json FROM app_config WHERE id = 'app' LIMIT 1`,
        );
        if (existing.length > 0) {
          const nextExtra = writePrintBranding(existing[0].extra_json ?? null, brandingDraft);
          await db.execute(
            `UPDATE app_config SET extra_json = ?, updated_at = ?, updated_by = ? WHERE id = 'app'`,
            [nextExtra, now, user?.uid ?? null],
          );
        }
      }
      setSaveStatus('saved');
    } catch (err) {
      console.error('[InvoicePrintWizard] save failed', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [db, orgId, user, draft, savedRow, brandingDraft]);

  // Print Preview renders the sample invoice into an isolated iframe (not a
  // hidden on-page mount), so the host page never leaks into the printout on
  // tablet/mobile.
  const handlePrintPreview = useCallback(() => {
    const html = renderToStaticMarkup(
      <InvoicePrintLayout
        settings={effective}
        patient={{ name: 'John Doe', age: '34 yrs', sex: 'M', patientNumber: 'P-0001' }}
        meta={{
          invoiceNumber: 'INV-2026-0001',
          invoiceDateIso: new Date().toISOString(),
          visitDateIso: new Date().toISOString(),
          providerName: 'Dr. Sample Provider',
          status: 'Issued',
        }}
        items={SAMPLE_ITEMS}
        payments={SAMPLE_PAYMENTS}
        totals={SAMPLE_TOTALS}
        formatMoney={sampleMoney}
        accentColor={primary}
        fontColor={effectiveFontColor}
        letterhead={effectiveLetterhead}
      />,
    );
    printHtmlViaIframe(html, 'Invoice');
  }, [effective, primary, effectiveFontColor, effectiveLetterhead]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Invoice Print"
        subtitle="Configure how invoice printouts look — choose a template, set branding, then save."
        collapsed={collapsed}
        onToggle={onToggleSidebar}
      />
      <div style={{ padding: '0 var(--sp-5) var(--sp-3)' }}>
        <StepIndicator currentStep={step} primary={primary} onStepClick={(s) => setStep(s)} />
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'minmax(420px, 520px) 1fr',
          gap: 'var(--sp-5)',
          padding: '0 var(--sp-5) var(--sp-5)',
          alignItems: 'stretch',
        }}
      >
        {/* Left: step content */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-5)' }}>
            {step === 1 && (
              <TemplateStep
                templateId={effective.templateId}
                primary={primary}
                onSelect={(id) => setDraftPatch({ templateId: id })}
              />
            )}
            {step === 2 && (
              <BrandingStep
                settings={effective}
                onChange={setDraftPatch}
                primary={primary}
                branding={effectiveBranding}
                onBrandingChange={patchBranding}
                showLetterheadFields={effective.templateId === 'classic_letterhead'}
              />
            )}
            {step === 3 && <SectionsStep settings={effective} onChange={setDraftPatch} />}
            {step === 4 && (
              <SaveStep
                saving={saving}
                saveStatus={saveStatus}
                onSave={handleSave}
                primary={primary}
              />
            )}
          </div>

          <div
            style={{
              borderTop: '1px solid var(--border)',
              padding: 'var(--sp-3) var(--sp-5)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--bg-surface-2, #fafafa)',
            }}
          >
            <button
              type="button"
              onClick={() => setStep((s) => (s > 1 ? ((s - 1) as StepNum) : s))}
              disabled={step === 1}
              style={{
                height: 34,
                padding: '0 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: step === 1 ? 'var(--text-disabled)' : 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                cursor: step === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Back
            </button>
            {step < 4 ? (
              <button
                type="button"
                onClick={() => setStep((s) => (s + 1) as StepNum)}
                style={{
                  height: 34,
                  padding: '0 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: primary,
                  color: '#fff',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  cursor: 'pointer',
                }}
              >
                Next
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={handlePrintPreview}
                  style={{
                    height: 34,
                    padding: '0 14px',
                    borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${primary}`,
                    background: 'transparent',
                    color: primary,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <NavIcon name="file-text" /> Print Preview
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    height: 34,
                    padding: '0 16px',
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: primary,
                    color: '#fff',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? 'Saving…' : 'Save Layout'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: live preview */}
        <PreviewPane
          settings={effective}
          accentColor={primary}
          fontColor={effectiveFontColor}
          letterhead={effectiveLetterhead}
        />
      </div>
    </div>
  );
}

// ─── Step indicator ───

function StepIndicator({
  currentStep,
  primary,
  onStepClick,
}: {
  currentStep: StepNum;
  primary: string;
  onStepClick: (step: StepNum) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {STEPS.map((s, idx) => {
        const isActive = s.num === currentStep;
        const isDone = s.num < currentStep;
        return (
          <div key={s.num} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <button
              type="button"
              onClick={() => onStepClick(s.num)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: isActive ? primary : isDone ? 'var(--text)' : 'var(--text-muted)',
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-semi)',
                  background: isActive
                    ? primary
                    : isDone
                      ? 'var(--bg-surface-2, #f5f5f5)'
                      : 'transparent',
                  color: isActive ? '#fff' : isDone ? primary : 'var(--text-muted)',
                  border: isActive
                    ? `1px solid ${primary}`
                    : `1px solid ${isDone ? primary : 'var(--border)'}`,
                }}
              >
                {isDone ? '✓' : s.num}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: isActive ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
                }}
              >
                {s.label}
              </span>
            </button>
            {idx < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: s.num < currentStep ? primary : 'var(--border)',
                  margin: '0 4px',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1: Template ───

function TemplateStep({
  templateId,
  primary,
  onSelect,
}: {
  templateId: InvoicePrintTemplateId;
  primary: string;
  onSelect: (id: InvoicePrintTemplateId) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <StepHeading
        title="Choose a system template"
        description="Pick a starting layout. You can change templates anytime — your branding and section choices will carry over."
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        {INVOICE_SYSTEM_TEMPLATES.map((t) => (
          <TemplateCard
            key={t.id}
            template={t}
            selected={t.id === templateId}
            primary={primary}
            onSelect={() => onSelect(t.id)}
          />
        ))}
      </div>
    </div>
  );
}

function TemplateCard({
  template,
  selected,
  primary,
  onSelect,
}: {
  template: InvoiceSystemTemplate;
  selected: boolean;
  primary: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        textAlign: 'left',
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: `1.5px solid ${selected ? primary : 'var(--border)'}`,
        background: selected ? `${primary}10` : 'var(--bg-surface)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {template.label}
        </span>
        {selected && <span style={{ color: primary, fontSize: 14, fontWeight: 700 }}>✓</span>}
      </div>
      <span
        style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', lineHeight: 1.4 }}
      >
        {template.description}
      </span>
    </button>
  );
}

// ─── Step 2: Branding ───

function BrandingStep({
  settings,
  onChange,
  primary,
  branding,
  onBrandingChange,
  showLetterheadFields,
}: {
  settings: InvoicePrintSettingsUI;
  onChange: (patch: Partial<InvoicePrintSettingsUI>) => void;
  primary: string;
  branding: PrintBrandingConfig;
  onBrandingChange: (patch: Partial<PrintBrandingConfig>) => void;
  showLetterheadFields: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <StepHeading
        title="Branding"
        description="These appear in the invoice's header and footer. Pick which configured logo to use — text-only headers also work."
      />
      <FieldGroup
        label="Print Font Color"
        hint="Applies to printed text + template chrome (used by Letterhead Classic)."
      >
        <PrintFontColorInput
          value={branding.printFontColor}
          onChange={(v) => onBrandingChange({ printFontColor: v })}
        />
      </FieldGroup>

      {showLetterheadFields && <LetterheadFields branding={branding} onChange={onBrandingChange} />}

      <FieldGroup label="Logo">
        <LogoSourcePicker
          value={settings.logoSource}
          onChange={(v) => onChange({ logoSource: v })}
          primary={primary}
        />
      </FieldGroup>
      <FieldGroup
        label="Clinic / Business Name (optional)"
        hint="Leave blank if your horizontal logo already shows it."
      >
        <TextInput value={settings.clinicName} onChange={(v) => onChange({ clinicName: v })} />
      </FieldGroup>
      <FieldGroup label="Clinic Address">
        <TextArea
          value={settings.clinicAddress}
          rows={2}
          onChange={(v) => onChange({ clinicAddress: v })}
        />
      </FieldGroup>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        <FieldGroup label="Phone">
          <TextInput value={settings.clinicPhone} onChange={(v) => onChange({ clinicPhone: v })} />
        </FieldGroup>
        <FieldGroup label="Email">
          <TextInput value={settings.clinicEmail} onChange={(v) => onChange({ clinicEmail: v })} />
        </FieldGroup>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        <FieldGroup label="Website">
          <TextInput
            value={settings.clinicWebsite}
            placeholder="www.example.com"
            onChange={(v) => onChange({ clinicWebsite: v })}
          />
        </FieldGroup>
        <FieldGroup label="Tax / Business ID" hint="e.g. GSTIN, VAT, BIR TIN.">
          <TextInput value={settings.taxId} onChange={(v) => onChange({ taxId: v })} />
        </FieldGroup>
      </div>
      <FieldGroup label="Footer Text">
        <TextArea
          value={settings.footerText}
          rows={2}
          placeholder="e.g. Thank you for your visit / payment terms / refund policy."
          onChange={(v) => onChange({ footerText: v })}
        />
      </FieldGroup>
    </div>
  );
}

// ─── Logo source picker ───

function LogoSourcePicker({
  value,
  onChange,
  primary,
}: {
  value: InvoicePrintLogoSource;
  onChange: (v: InvoicePrintLogoSource) => void;
  primary: string;
}) {
  const options: Array<{
    value: InvoicePrintLogoSource;
    label: string;
    hint: string;
    url: string;
  }> = [
    {
      value: 'standard',
      label: 'Square Logo',
      hint: orgLogoUrl ? 'Configured for this clinic' : 'Not configured for this build',
      url: orgLogoUrl,
    },
    {
      value: 'horizontal',
      label: 'Horizontal Logo',
      hint: orgLogoHorizontalUrl ? 'Configured for this clinic' : 'Not configured for this build',
      url: orgLogoHorizontalUrl,
    },
    { value: 'none', label: 'No Logo', hint: 'Text-only header', url: '' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--sp-2)' }}>
      {options.map((opt) => {
        const selected = value === opt.value;
        const disabled = (opt.value === 'standard' || opt.value === 'horizontal') && !opt.url;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => !disabled && onChange(opt.value)}
            disabled={disabled}
            aria-pressed={selected}
            style={{
              textAlign: 'left',
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              border: `1.5px solid ${selected ? primary : 'var(--border)'}`,
              background: selected
                ? `${primary}10`
                : disabled
                  ? 'var(--bg-surface-2, #fafafa)'
                  : 'var(--bg-surface)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              minHeight: 96,
            }}
          >
            <div
              style={{
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fff',
                border: '1px dashed var(--border)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {opt.url ? (
                <img
                  src={opt.url}
                  alt=""
                  style={{
                    maxHeight: 40,
                    maxWidth: opt.value === 'horizontal' ? 140 : 40,
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    fontStyle: 'italic',
                  }}
                >
                  {opt.value === 'none' ? 'No logo' : 'Not set'}
                </span>
              )}
            </div>
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {opt.label}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                {opt.hint}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Step 3: Sections ───

function SectionsStep({
  settings,
  onChange,
}: {
  settings: InvoicePrintSettingsUI;
  onChange: (patch: Partial<InvoicePrintSettingsUI>) => void;
}) {
  const groups: Array<{
    title: string;
    toggles: Array<{
      key: keyof InvoicePrintSettingsUI;
      label: string;
      description: string;
    }>;
  }> = [
    {
      title: 'Header / patient',
      toggles: [
        {
          key: 'showPatientDemographics',
          label: 'Patient demographics',
          description: 'Name, age, sex',
        },
        {
          key: 'showPatientNumber',
          label: 'Patient number',
          description: 'Show the clinic-issued patient #',
        },
        {
          key: 'showInvoiceNumber',
          label: 'Invoice number',
          description: 'Show the invoice reference number',
        },
        {
          key: 'showInvoiceDate',
          label: 'Invoice date',
          description: 'Date the invoice was issued',
        },
        {
          key: 'showVisitDate',
          label: 'Visit / encounter date',
          description: 'Useful for retrospective billing',
        },
        {
          key: 'showProvider',
          label: 'Attending provider',
          description: 'Shown in the meta strip',
        },
      ],
    },
    {
      title: 'Line items columns',
      toggles: [
        {
          key: 'showItemQuantity',
          label: 'Quantity column',
          description: 'Number of units per item',
        },
        { key: 'showItemUnitPrice', label: 'Rate column', description: 'Unit price per item' },
        {
          key: 'showItemDiscount',
          label: 'Discount column',
          description: 'Per-line discount amount',
        },
        { key: 'showItemTax', label: 'Tax column', description: 'Per-line tax rate (%)' },
        { key: 'showItemAmount', label: 'Amount column', description: 'Final line amount' },
      ],
    },
    {
      title: 'Totals / settlement',
      toggles: [
        { key: 'showSubtotal', label: 'Subtotal', description: 'Sum of items before adjustments' },
        {
          key: 'showDiscountTotal',
          label: 'Discount total',
          description: 'Aggregate of line discounts',
        },
        { key: 'showTaxTotal', label: 'Tax total', description: 'Aggregate tax amount' },
        {
          key: 'showPayments',
          label: 'Payments summary',
          description: 'List of payments received',
        },
        {
          key: 'showBalanceDue',
          label: 'Balance due',
          description: 'Highlighted balance / paid-in-full pill',
        },
      ],
    },
    {
      title: 'Footer',
      toggles: [
        { key: 'showFooterNotes', label: 'Footer notes', description: 'Free-text footer block' },
        {
          key: 'showSignatureBlock',
          label: 'Signature block',
          description: 'Authorized signature line',
        },
      ],
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <StepHeading
        title="Print sections"
        description="Toggle which blocks appear on the printout. Hidden sections are skipped in the generated layout."
      />
      {groups.map((group) => (
        <div key={group.title}>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 4,
            }}
          >
            {group.title}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {group.toggles.map((t) => (
              <ToggleRow
                key={t.key}
                label={t.label}
                description={t.description}
                checked={Boolean(settings[t.key])}
                onChange={(v) => onChange({ [t.key]: v } as Partial<InvoicePrintSettingsUI>)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Step 4: Save ───

function SaveStep({
  saving,
  saveStatus,
  onSave,
  primary,
}: {
  saving: boolean;
  saveStatus: 'idle' | 'saved' | 'error';
  onSave: () => void;
  primary: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <StepHeading
        title="Preview & save"
        description="Review the live preview on the right. Save to make this the clinic's default invoice print layout."
      />
      <div
        style={{
          padding: 'var(--sp-3)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-surface-2, #fafafa)',
          border: '1px solid var(--border)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          lineHeight: 1.5,
        }}
      >
        Saving stores this configuration as the org default. The live Billing / Invoice workspace's
        Print Invoice action will then use this saved layout to render every printable invoice.
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        style={{
          alignSelf: 'flex-start',
          height: 36,
          padding: '0 16px',
          borderRadius: 'var(--radius-sm)',
          border: 'none',
          background: primary,
          color: '#fff',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? 0.6 : 1,
        }}
      >
        {saving ? 'Saving…' : 'Save as Default Layout'}
      </button>
      {saveStatus === 'saved' && (
        <div
          style={{ color: 'var(--color-success-text, #15803d)', fontSize: 'var(--font-size-sm)' }}
        >
          Layout saved.
        </div>
      )}
      {saveStatus === 'error' && (
        <div style={{ color: '#dc2626', fontSize: 'var(--font-size-sm)' }}>
          Save failed. Check the console for details.
        </div>
      )}
    </div>
  );
}

// ─── Preview pane (A4 scale-to-fit) ───

function PreviewPane({
  settings,
  accentColor,
  fontColor,
  letterhead,
}: {
  settings: InvoicePrintSettingsUI;
  accentColor: string;
  fontColor: string;
  letterhead: PrintLetterheadInfo;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(1, w / A4_WIDTH_PX));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      style={{
        background: 'var(--bg-surface-2, #f5f5f5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-4)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          marginBottom: 'var(--sp-3)',
          fontWeight: 'var(--font-weight-semi)',
        }}
      >
        Live Preview
      </div>
      <div
        ref={frameRef}
        style={{
          width: '100%',
          height: A4_HEIGHT_PX * scale,
          position: 'relative',
          alignSelf: 'center',
          maxWidth: A4_WIDTH_PX,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: A4_WIDTH_PX,
            transformOrigin: 'top left',
            transform: `scale(${scale})`,
          }}
        >
          <InvoicePrintLayout
            settings={settings}
            patient={{ name: 'John Doe', age: '34 yrs', sex: 'M', patientNumber: 'P-0001' }}
            meta={{
              invoiceNumber: 'INV-2026-0001',
              invoiceDateIso: new Date().toISOString(),
              visitDateIso: new Date().toISOString(),
              providerName: 'Dr. Sample Provider',
              status: 'Issued',
            }}
            items={SAMPLE_ITEMS}
            payments={SAMPLE_PAYMENTS}
            totals={SAMPLE_TOTALS}
            formatMoney={sampleMoney}
            accentColor={accentColor}
            fontColor={fontColor}
            letterhead={letterhead}
            previewMode
          />
        </div>
      </div>
    </div>
  );
}

// ─── Atoms ───

function StepHeading({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2
        style={{
          margin: 0,
          fontSize: 'var(--font-size-base)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {title}
      </h2>
      <p
        style={{
          margin: '4px 0 0',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
        }}
      >
        {description}
      </p>
    </div>
  );
}

function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-placeholder, var(--text-muted))',
          }}
        >
          {hint}
        </span>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  height: 34,
  padding: '0 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}

// PRD.284 — letterhead free-text fields, shown when the Letterhead Classic
// template is selected. Left header column: doctor name + qualifications /
// specialty / designation. Right header column: registration / panel / address.
function LetterheadFields({
  branding,
  onChange,
}: {
  branding: PrintBrandingConfig;
  onChange: (patch: Partial<PrintBrandingConfig>) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #f8fafc)',
      }}
    >
      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semi)' }}>
        Letterhead header
      </div>
      <FieldGroup
        label="Doctor name (left header)"
        hint="The large name in the left column. Leave blank to use the invoice's attending provider."
      >
        <TextInput
          value={branding.letterheadDoctorName}
          placeholder="e.g. Dr. M. Shanmugam"
          onChange={(v) => onChange({ letterheadDoctorName: v })}
        />
      </FieldGroup>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        <FieldGroup label="Qualifications (left)">
          <TextInput
            value={branding.doctorQualifications}
            placeholder="e.g. MBBS, MD"
            onChange={(v) => onChange({ doctorQualifications: v })}
          />
        </FieldGroup>
        <FieldGroup label="Specialty (left)">
          <TextInput
            value={branding.doctorSpecialty}
            placeholder="e.g. Consultant Chest Diseases"
            onChange={(v) => onChange({ doctorSpecialty: v })}
          />
        </FieldGroup>
      </div>
      <FieldGroup label="Designation (left)">
        <TextInput
          value={branding.doctorDesignation}
          placeholder="e.g. Joint Director of Medical Services (Retd)"
          onChange={(v) => onChange({ doctorDesignation: v })}
        />
      </FieldGroup>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        <FieldGroup label="Registration no. (right)">
          <TextInput
            value={branding.registrationNumber}
            placeholder="e.g. 22930"
            onChange={(v) => onChange({ registrationNumber: v })}
          />
        </FieldGroup>
        <FieldGroup label="Additional registrations (right)">
          <TextInput
            value={branding.additionalRegistrations}
            placeholder="e.g. Medical Examiner LIC, PLI & RPLI"
            onChange={(v) => onChange({ additionalRegistrations: v })}
          />
        </FieldGroup>
      </div>
      <FieldGroup label="Insurance / Panel (right)">
        <TextInput
          value={branding.insurancePanel}
          placeholder="e.g. Authorized Medical Attendant, SBI"
          onChange={(v) => onChange({ insurancePanel: v })}
        />
      </FieldGroup>
      <FieldGroup label="Additional footer lines" hint="Shown centered in the footer.">
        <TextArea
          value={branding.footerExtraText}
          rows={2}
          onChange={(v) => onChange({ footerExtraText: v })}
        />
      </FieldGroup>
    </div>
  );
}

// PRD.284 — color swatch + hex input for the print font color.
function PrintFontColorInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <input
        type="color"
        aria-label="Print font color"
        value={normalizePrintFontColor(value)}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 44,
          height: 34,
          padding: 0,
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--bg-surface)',
          cursor: 'pointer',
        }}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        style={{ ...inputStyle, maxWidth: 140 }}
      />
    </div>
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows = 2,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        ...inputStyle,
        height: 'auto',
        padding: '8px 10px',
        resize: 'vertical',
        fontFamily: 'inherit',
        lineHeight: 'var(--line-height-body)',
      }}
    />
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) 0',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
      }}
    >
      <div>
        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)' }}>
          {label}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {description}
        </div>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, marginTop: 2, cursor: 'pointer' }}
      />
    </label>
  );
}
